import { useCallback, useEffect, useState, type FC } from "react";

import { Nudge } from "@/components/nudges/Nudge";
import { DESKTOP_APP_DOWNLOAD_URL } from "@/desktop";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { TrackLocations } from "@/hooks/use-segment";
import { useSetPreference } from "@/queries/user-preferences-queries";

export const DownloadAppNudge: FC = () => {
    const { active: nudge, error, isPending } = useNudges();
    const { mutate: setPreference } = useSetPreference();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (isPending || error) {
            return;
        }

        if (nudge?.key === NudgeKeys.DOWNLOAD_APP) {
            setOpen(true);
            return;
        }
        setOpen(false);
    }, [isPending, error, nudge?.key]);

    const toggleOpen = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setPreference({
                    key: NudgeKeys.DOWNLOAD_APP,
                    value: new Date().toISOString(),
                });
            }
            return isNowOpen;
        });
    }, [setPreference]);

    const ariaLabelPrefix = "Suggestion - Download App";

    return (
        <Nudge
            open={open}
            toggleOpen={toggleOpen}
            ariaLabelPrefix={ariaLabelPrefix}
            data-testid="nudge-download-app-trigger"
            data-track-location={TrackLocations.NudgeDownloadAppPopover}
        >
            <Nudge.Content>
                <Nudge.Header>
                    <Nudge.Title>Try Ona for Mac</Nudge.Title>
                    <Nudge.Description>Get a faster, native experience with the Ona desktop app.</Nudge.Description>
                </Nudge.Header>
                <Nudge.Action
                    variant="primary"
                    aria-label={`${ariaLabelPrefix} - Download`}
                    asChild
                    data-tracking-id="nudge-download-app-download"
                >
                    <a href={DESKTOP_APP_DOWNLOAD_URL} target="_blank" rel="noreferrer">
                        Download for Mac
                    </a>
                </Nudge.Action>
            </Nudge.Content>
        </Nudge>
    );
};
